import {useMemo} from 'react'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {
  branchFilterDefinition,
  keyOnlyBranchFilterDefinition,
  type FilterDefinition,
  type FilterObject,
} from '../components/insights/SecurityProductsFilterBar'
import type {SourceType} from '@github-ui/repos-rules-types/rules-types'
import {useBranches} from './use-branches'
import {useIsStafftools} from './use-is-stafftools'

type UseBranchFilterDefinitionsOptions = {
  sourceType: SourceType
  ownerLogin: string
  repoName: string
  branchIsValid?: boolean
  filterBranch?: string
}

type UseBranchFilterDefinitionsResult<T extends FilterObject> = {
  definitions: Array<FilterDefinition<T>>
  showBranchFilter: boolean
  showKeyOnlyBranchFilter: boolean
}

/**
 * Encapsulates the branch filter logic shared across filter bars (InsightsFilterBar2,
 * DashboardFilterBar, etc.). Determines which branch filter variant to show based on
 * sourceType, stafftools context, and feature flags, fetches branches when needed,
 * and returns ready-to-use FilterDefinition entries.
 */
export function useBranchFilterDefinitions<T extends FilterObject>({
  sourceType,
  ownerLogin,
  repoName,
  branchIsValid,
  filterBranch,
}: UseBranchFilterDefinitionsOptions): UseBranchFilterDefinitionsResult<T> {
  const rulesInsightsStafftoolsBranches = isFeatureEnabled('rules_insights_stafftools_branches')
  const isStafftools = useIsStafftools()
  const isRepo = sourceType === 'repository'
  const showBranchFilter = isRepo && (!isStafftools || rulesInsightsStafftoolsBranches)
  const showKeyOnlyBranchFilter = isRepo && isStafftools && !rulesInsightsStafftoolsBranches

  const fetchedBranches = useBranches(showBranchFilter ? ownerLogin : '', showBranchFilter ? repoName : '')

  // If the server confirmed the branch from the URL is valid, include it in the list
  // so validation passes even before the async branch fetch completes.
  // Invalid branches are excluded so the user sees a validation error.
  const branches = useMemo(() => {
    if (!branchIsValid || !filterBranch || fetchedBranches.includes(filterBranch)) return fetchedBranches
    return [filterBranch, ...fetchedBranches]
  }, [branchIsValid, fetchedBranches, filterBranch])

  const definitions = useMemo<Array<FilterDefinition<T>>>(
    () =>
      showBranchFilter
        ? [branchFilterDefinition<T>(branches)]
        : showKeyOnlyBranchFilter
          ? [keyOnlyBranchFilterDefinition<T>()]
          : [],
    [showBranchFilter, showKeyOnlyBranchFilter, branches],
  )

  return {definitions, showBranchFilter, showKeyOnlyBranchFilter}
}
